import {
  type Field,
  Form as FormComponent,
  type FormRef,
  type Step,
  useActiveForm,
} from '@atlas/react-forms';
import { forwardRef, useCallback, useMemo } from 'react';

import fields from './fields';
import type { FormProps } from './types';
import useAsyncValidation from './useAsyncValidation';

import FormContainer from './FormContainer';

const Form = forwardRef<FormRef, FormProps>(
  (
    {
      handleSubmit,
      onError,
      onSubmit: onSubmitHandler,
      beforeSubmitButton,
      afterSubmitButton,
      getActiveForms,
      formType,
      submitButtonText,
      initialValues,
      showErrorFields,
      getAsyncValidateFields,
      ...props
    },
    formRef,
  ) => {
    const {
      activeType,
      setActiveStep,
      activeStep = 0,
      stepsAmount,
      submit,
    } = useActiveForm(formType, handleSubmit, getActiveForms);

    // formId possibly be undefined when the request resolves with an empty array
    const formId = activeType?.id;

    const { validateField } = useAsyncValidation(formId);
    const isLastStep = useMemo(() => {
      return (
        stepsAmount && activeType && activeStep === activeType.steps.length - 1
      );
    }, [activeStep, stepsAmount, activeType]);

    const action = useMemo(
      () => (isLastStep ? submit : () => setActiveStep(activeStep + 1)),
      [setActiveStep, submit, isLastStep, activeStep],
    );

    const steps = activeType?.steps as Step[];
    const currentSteps = activeType?.steps[activeStep] as Step;

    const validateFieldAsync = useCallback(
      async (field: Field) => {
        const result = await validateField(field);

        getAsyncValidateFields?.({
          isAsyncValid: !result.length,
          name: field.name,
        });

        return result;
      },
      [validateField, getAsyncValidateFields],
    );

    return (
      <>
        {activeType && (
          <FormComponent
            ref={formRef}
            fieldComponents={fields}
            ContainerComponent={FormContainer}
            onSubmit={onSubmitHandler}
            onValidateFieldAsync={validateFieldAsync}
            // @ts-ignore
            action={action}
            onError={onError}
            steps={steps}
            form={currentSteps}
            ContainerComponentProps={{
              beforeSubmitButton,
              afterSubmitButton,
              submitButtonText,
              showErrorFields,
              ...props,
            }}
            initialValues={initialValues}
          />
        )}
      </>
    );
  },
);

export default Form;
